import toast from 'react-hot-toast';
import API_ENDPOINTS from '../config/apiConfig';
import { api } from './api';
import { ensureIntercomBooted, showIntercom, showIntercomNewMessage } from './intercom';

/*
 * "Convert to a support ticket": a fast model drafts the first support message
 * from the Help Center conversation (azfApi/helpCenter/ticket.js), with a link
 * to the page the user was working on, and Intercom opens with it pre-filled.
 * The user reviews and sends it.
 */

const LAST_PAGE_KEY = 'lodgeic:lastAppPage';

/** Remember the last page outside the Help Center, so a ticket can link to it. */
export function rememberAppPage(pathWithSearch) {
  if (!pathWithSearch || pathWithSearch.startsWith('/help')) return;
  try { sessionStorage.setItem(LAST_PAGE_KEY, pathWithSearch); } catch { /* storage blocked */ }
}

export function lastAppPage() {
  try { return sessionStorage.getItem(LAST_PAGE_KEY); } catch { return null; }
}

function templateDraft({ question, articleTitle, pageUrl }) {
  const lead = question
    ? `I need help with this: ${question}`
    : `I read the Help Center guide “${articleTitle}” and still need help.`;
  return question && pageUrl ? `${lead}\n\nPage: ${pageUrl}` : lead;
}

/**
 * @param {object} p
 * @param {string} [p.question]      the latest question
 * @param {Array}  [p.history]       prior [{ role, content }] turns
 * @param {string} [p.articleTitle]  the guide the user was reading
 * @param {string} [p.orgName]
 * @param {string} [p.pagePath]      defaults to the last page outside the Help Center
 * @returns {Promise<boolean>} true when the messenger opened
 */
export async function convertToSupportTicket({ question = '', history = [], articleTitle = null, orgName = null, pagePath = null } = {}) {
  // Nothing to draft from: just open the chat, with nothing pre-filled.
  if (!question && !history.length && !articleTitle) {
    const ready = await ensureIntercomBooted();
    if (!ready || !showIntercom()) toast.error('Support chat is unavailable right now. Email support@lodgeic.io.');
    return !!ready;
  }
  const path = pagePath || lastAppPage();
  const pageUrl = path ? `${window.location.origin}${path}` : null;
  let draft = null;
  // Nothing described yet: a sentence to finish beats a model guessing.
  if (question || history.length) {
    const pending = toast.loading('Drafting your message…');
    try {
      const res = await api.post(`${API_ENDPOINTS.HELP_CENTER}/ticketDraft`, { question, history, pageUrl, orgName, articleTitle });
      draft = res.data?.draft || null;
    } catch { /* fall back to the template */ } finally {
      toast.dismiss(pending);
    }
  }
  if (!draft) draft = templateDraft({ question, articleTitle, pageUrl });
  const ready = await ensureIntercomBooted();
  if (!ready || !showIntercomNewMessage(draft)) {
    toast.error('Support chat is unavailable right now. Email support@lodgeic.io.');
    return false;
  }
  return true;
}
